import React, { useState } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { Box, Button, TextField, Typography, Alert, CircularProgress } from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';

const MAX_FILE_SIZE_MB = 10;

const ContactForm: React.FC = () => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(`File size must be less than ${MAX_FILE_SIZE_MB}MB.`);
        setFile(null);
        return;
      }
      setFile(selected);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess(null);
    setError(null);
    
    // Client-side validation
    if (name.trim().length < 2) {
      setError('Name must be at least 2 characters long.');
      setSubmitting(false);
      return;
    }
    if (message.trim().length < 10) {
      setError('Message must be at least 10 characters long.');
      setSubmitting(false);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('email', email);
      formData.append('message', message);
      if (file) formData.append('file', file);

      const res = await fetch(API_ENDPOINTS.CONTACT, {
        method: 'POST',
        body: formData,
        credentials: 'include', // Required for CORS with credentials
      });
      if (res.ok) {
        setSuccess('Your message has been sent!');
        setName('');
        setEmail('');
        setMessage('');
        setFile(null);
      } else {
        const data = await res.json();
        console.error('Contact form error:', data);
        if (data.details && Array.isArray(data.details)) {
          setError(data.details.join(', '));
        } else {
          setError(data.error || 'Failed to send message.');
        }
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Failed to send message. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        color="primary"
        sx={{ fontWeight: 600 }}
      >
        {t('contact.form.title')}
      </Typography>
      {success && <Alert severity="success">{success}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label={t('contact.form.name')}
        value={name}
        onChange={e => setName(e.target.value)}
        fullWidth
        required
        margin="normal"
      />
      <TextField
        label={t('contact.form.email')}
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        fullWidth
        required
        margin="normal"
      />
      <TextField
        label={t('contact.form.message')}
        value={message}
        onChange={e => setMessage(e.target.value)}
        fullWidth
        required
        multiline
        rows={4}
        margin="normal"
      />
      <Button
        variant="contained"
        component="label"
        sx={{ mt: 2 }}
      >
        {file ? `File: ${file.name}` : 'Attach STL/ZIP File'}
        <input
          type="file"
          hidden
          accept=".stl,.zip,application/zip,application/vnd.ms-pkistl,application/octet-stream"
          onChange={handleFileChange}
        />
      </Button>
      <Box sx={{ mt: 3 }}>
        <Button type="submit" variant="contained" color="primary" disabled={submitting}>
          {submitting ? <CircularProgress size={24} /> : t('contact.form.submit')}
        </Button>
      </Box>
    </Box>
  );
};

export default ContactForm; 